import { Link } from "react-router-dom";
import { PublicLayout } from "@/components/public/PublicLayout";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import {
  ShieldCheck, Globe, Stethoscope, Hospital, Eye, Ear, UserCheck,
  Search, ListChecks, Smartphone, Handshake, ArrowRight, Lock, HeartHandshake,
  BadgeCheck, Clock, Sparkles, Plane, MapPin,
} from "lucide-react";
import { Tooth } from "@/components/icons/Tooth";

const chips = [
  { icon: Clock, text: "Devis en 2 minutes" },
  { icon: HeartHandshake, text: "Un courtier vous accompagne" },
  { icon: BadgeCheck, text: "Sans engagement" },
  { icon: Lock, text: "Vos données protégées" },
];

const steps = [
  { icon: ListChecks, title: "Décrivez votre situation", text: "Quelques questions simples, adaptées à votre profil. Aucune donnée médicale détaillée.", color: "from-indigo-400 to-indigo-600" },
  { icon: Smartphone, title: "Confirmez votre portable", text: "Une vérification rapide par SMS ou WhatsApp sécurise l'accès à votre comparatif personnalisé.", color: "from-[#8488C6] to-[#4A4FA0]" },
  { icon: Search, title: "Comparez les solutions", text: "On rapproche vos besoins des garanties réelles des contrats disponibles.", color: "from-[#A99BC6] to-[#8E7FB0]" },
  { icon: Handshake, title: "Un conseiller vous guide", text: "Devis ou rappel : un courtier valide les offres réellement disponibles.", color: "from-[#8793B5] to-[#5A6490]" },
];

const guarantees = [
  { icon: Hospital, label: "Hospitalisation" }, { icon: Stethoscope, label: "Soins courants" },
  { icon: Eye, label: "Optique" }, { icon: Tooth, label: "Dentaire" },
  { icon: Ear, label: "Audiologie" }, { icon: Globe, label: "Assistance & rapatriement" },
];

const faqs = [
  { q: "Pourquoi dois-je vérifier mon numéro de portable ?", a: "La vérification par SMS ou WhatsApp sécurise l'accès à votre comparatif personnalisé et garantit la fiabilité des échanges. Elle ne constitue pas un consentement à recevoir des publicités." },
  { q: "Les tarifs affichés sont-ils réels ?", a: "Nous n'affichons jamais de faux tarifs ni de faux contrats. Lorsqu'aucune connexion fournisseur n'est active, nous vous proposons une étude personnalisée réalisée par un conseiller." },
  { q: "Quelle différence entre mutuelle France et assurance expatrié ?", a: "La mutuelle santé complète le régime français pour les résidents en France. L'assurance expatrié couvre les personnes vivant à l'étranger, au premier euro ou en complément de la CFE." },
  { q: "Mes données sont-elles protégées ?", a: "Vos informations sont stockées de façon sécurisée, utilisées uniquement pour traiter votre demande et ne sont jamais revendues. Vous pouvez demander leur suppression." },
];

export default function Home() {
  return (
    <PublicLayout>
      {/* ================= HERO ================= */}
      <section className="relative overflow-hidden" style={{ background: "linear-gradient(160deg,#1C1F45 0%,#24285A 55%,#2E3370 100%)" }}>
        {/* animated color blobs */}
        <div className="pointer-events-none absolute inset-0 overflow-hidden">
          <div className="absolute -top-20 -left-16 w-80 h-80 rounded-full bg-indigo-500/25 blur-3xl animate-blob" />
          <div className="absolute top-32 right-0 w-96 h-96 rounded-full bg-[#7E82C4]/20 blur-3xl animate-blob" style={{ animationDelay: "3s" }} />
          <div className="absolute bottom-0 left-1/3 w-72 h-72 rounded-full bg-[#9B86B5]/15 blur-3xl animate-blob" style={{ animationDelay: "6s" }} />
        </div>

        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-14 md:pt-20 pb-16 md:pb-24">
          <div className="animate-fade-up max-w-3xl mx-auto text-center">
            <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.18em] text-[#C6C8F0] bg-white/10 backdrop-blur px-4 py-1.5 rounded-full border border-white/10">
              <Sparkles size={14} /> Comparateur santé nouvelle génération
            </span>
            <h1 data-testid="hero-title" className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-white leading-[1.08] mt-5">
              Votre assurance en France comme à l'étranger,<br />
              <span className="text-gradient-warm">la bonne couverture</span><br />
              <span className="text-white">au juste prix.</span>
            </h1>
            <p className="text-lg text-slate-300 mt-6 leading-relaxed max-w-xl mx-auto">
              Comparez les solutions santé adaptées à votre situation et bénéficiez de l'accompagnement d'un courtier. Rapide, clair, et sans complications.
            </p>

            {/* chips */}
            <div className="mt-8 flex flex-wrap justify-center gap-x-6 gap-y-2">
              {chips.map((c) => (
                <span key={c.text} className="inline-flex items-center gap-1.5 text-sm text-slate-300"><c.icon size={15} className="text-[#9DA0DB]" />{c.text}</span>
              ))}
            </div>
          </div>

          {/* Parcours A & B mis en avant */}
          <div className="mt-14 grid md:grid-cols-2 gap-6 max-w-4xl mx-auto stagger text-left">
            <Link to="/simulateur/mutuelle-france" data-testid="parcours-france-button">
              <div className="group relative overflow-hidden rounded-3xl border-2 border-indigo-400/40 hover:border-indigo-300 transition-all duration-300 hover:-translate-y-1.5 hover:shadow-[0_28px_70px_-18px_rgba(74,79,160,0.6)] min-h-[260px] cursor-pointer h-full">
                <img src="https://images.unsplash.com/photo-1758691031787-90867cb6fb2c?crop=entropy&cs=srgb&fm=jpg&q=85&w=800" alt="Couple de seniors sereins" className="absolute inset-0 w-full h-full object-cover opacity-60 group-hover:opacity-75 group-hover:scale-105 transition-all duration-500" />
                <div className="absolute inset-0 bg-gradient-to-br from-[#1C1F45]/90 via-[#1C1F45]/60 to-indigo-950/30" />
                <div className="relative p-7 flex flex-col h-full">
                  <div className="flex items-center gap-3">
                    <span className="w-14 h-14 rounded-2xl bg-gradient-to-br from-indigo-400 to-indigo-700 flex items-center justify-center shadow-lg shadow-indigo-900/50"><ShieldCheck size={28} className="text-white" /></span>
                    <span className="text-xs font-bold uppercase tracking-wider text-indigo-200">Parcours A</span>
                  </div>
                  <p className="font-display font-bold text-white mt-5 text-2xl leading-tight">Mutuelle santé France</p>
                  <p className="text-sm text-slate-300 mt-1.5">Pour les résidents en France</p>
                  <span className="mt-auto pt-6 inline-flex items-center gap-2 text-sm font-bold text-[#2E3370] bg-white group-hover:bg-slate-100 w-fit px-5 py-2.5 rounded-full transition-colors">Comparer les mutuelles <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" /></span>
                </div>
              </div>
            </Link>
            <Link to="/simulateur/assurance-expat" data-testid="parcours-expat-button">
              <div className="group relative overflow-hidden rounded-3xl border-2 border-[#9B86B5]/50 hover:border-[#B3A0CC] transition-all duration-300 hover:-translate-y-1.5 hover:shadow-[0_28px_70px_-18px_rgba(155,134,181,0.6)] min-h-[260px] cursor-pointer h-full">
                <img src="https://images.unsplash.com/photo-1753250804362-9192013670c0?crop=entropy&cs=srgb&fm=jpg&q=85&w=800" alt="Famille expatriée" className="absolute inset-0 w-full h-full object-cover opacity-60 group-hover:opacity-75 group-hover:scale-105 transition-all duration-500" />
                <div className="absolute inset-0 bg-gradient-to-br from-[#1C1F45]/90 via-[#1C1F45]/60 to-[#3a2f52]/35" />
                <div className="relative p-7 flex flex-col h-full">
                  <div className="flex items-center gap-3">
                    <span className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#A99BC6] to-[#6E6FB2] flex items-center justify-center shadow-lg shadow-[#3a2f52]/50"><Plane size={28} className="text-white" /></span>
                    <span className="text-xs font-bold uppercase tracking-wider text-[#CDBCE0]">Parcours B</span>
                  </div>
                  <p className="font-display font-bold text-white mt-5 text-2xl leading-tight">Assurance expatrié</p>
                  <p className="text-sm text-slate-300 mt-1.5">Expatriés & familles à l'étranger</p>
                  <span className="mt-auto pt-6 inline-flex items-center gap-2 text-sm font-bold text-[#2E3370] bg-white group-hover:bg-slate-100 w-fit px-5 py-2.5 rounded-full transition-colors">Comparer les assurances <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" /></span>
                </div>
              </div>
            </Link>
          </div>
        </div>
        <div className="relative h-10 bg-white rounded-t-[2.5rem]" />
      </section>

      {/* ================= FONCTIONNEMENT ================= */}
      <section id="fonctionnement" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
        <div className="max-w-2xl">
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-indigo-500">Un parcours clair</span>
          <h2 className="font-display text-3xl lg:text-4xl font-bold text-slate-900 mt-2">Comment ça fonctionne, en 4 étapes</h2>
          <p className="text-slate-600 mt-4">De la simulation à l'accompagnement par un conseiller : un parcours clair, transparent et rapide.</p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mt-12 stagger">
          {steps.map((s, i) => (
            <Card key={i} className="group p-6 border-slate-100 hover:-translate-y-1.5 hover:shadow-xl transition-[transform,box-shadow] duration-200">
              <div className={`w-12 h-12 rounded-2xl bg-gradient-to-br ${s.color} flex items-center justify-center mb-4 shadow-lg`}>
                <s.icon className="text-white" size={22} />
              </div>
              <p className="text-xs font-mono-crm text-slate-300 font-bold">É T A P E&nbsp; 0{i + 1}</p>
              <h3 className="font-display font-bold text-slate-900 mt-1">{s.title}</h3>
              <p className="text-sm text-slate-600 mt-2 leading-relaxed">{s.text}</p>
            </Card>
          ))}
        </div>
      </section>

      {/* ================= GARANTIES ================= */}
      <section id="garanties" className="relative py-16 md:py-24 overflow-hidden" style={{ background: "linear-gradient(180deg,#F8FAFC 0%,#EEF4FF 100%)" }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
            <div>
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-indigo-600">Ce qui est couvert</span>
              <h2 className="font-display text-3xl lg:text-4xl font-bold text-slate-900 mt-2">Des garanties qui comptent vraiment</h2>
            </div>
            <Link to="/simulateur/mutuelle-france"><Button variant="outline" className="rounded-full">Voir ce qui me correspond <ArrowRight size={16} className="ml-1.5" /></Button></Link>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 mt-12 stagger">
            {guarantees.map((g) => (
              <Card key={g.label} className="p-6 flex flex-col items-center text-center hover:-translate-y-1 hover:shadow-lg transition-[transform,box-shadow] duration-200 bg-white/80 backdrop-blur border-white">
                <div className="w-12 h-12 rounded-full bg-indigo-50 flex items-center justify-center mb-3"><g.icon className="text-indigo-700" size={24} /></div>
                <span className="text-sm font-semibold text-slate-700">{g.label}</span>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* ================= ACCOMPAGNEMENT ================= */}
      <section id="accompagnement" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24 grid lg:grid-cols-2 gap-12 items-center">
        <div>
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-indigo-600">Humain avant tout</span>
          <h2 className="font-display text-3xl lg:text-4xl font-bold text-slate-900 mt-2">Un courtier dans votre équipe</h2>
          <p className="text-slate-600 mt-4 leading-relaxed">
            Un conseiller analyse votre situation, vérifie l'éligibilité des offres et vous oriente vers la solution la plus adaptée — jamais vers la commission la plus élevée.
          </p>
          <ul className="mt-6 space-y-3">
            {["Étude personnalisée de votre besoin", "Vérification des conditions d'éligibilité", "Explication claire des garanties et exclusions", "Suivi jusqu'à la souscription"].map((t) => (
              <li key={t} className="flex items-start gap-3 text-slate-700">
                <span className="w-6 h-6 rounded-full bg-indigo-100 flex items-center justify-center shrink-0 mt-0.5"><UserCheck className="text-indigo-600" size={14} /></span>{t}
              </li>
            ))}
          </ul>
          <Link to="/simulateur/mutuelle-france"><Button className="mt-8 btn-gradient border-0 rounded-full h-12 px-7 text-base" data-testid="cta-start-sim">Démarrer ma comparaison <ArrowRight size={18} className="ml-1.5" /></Button></Link>
        </div>
        <Card className="relative p-8 overflow-hidden border-indigo-100" style={{ background: "linear-gradient(135deg,#ffffff 0%,#EEF0FB 60%,#F3F1F9 100%)" }}>
          <div className="absolute -right-10 -top-10 w-40 h-40 rounded-full bg-indigo-200/40 blur-2xl" />
          <h3 className="font-display font-bold text-slate-900 text-xl relative">Prêt à comparer ?</h3>
          <p className="text-sm text-slate-600 mt-2 relative">Après votre simulation et la vérification de votre numéro de portable, demandez un devis ou un rappel. Un conseiller valide les offres réellement disponibles.</p>
          <div className="mt-6 grid gap-3 relative">
            <Link to="/simulateur/mutuelle-france"><Button variant="outline" className="w-full justify-between h-12 rounded-xl bg-white"><span className="flex items-center gap-2"><MapPin size={17} className="text-indigo-600" />Mutuelle santé France</span><ArrowRight size={16} /></Button></Link>
            <Link to="/simulateur/assurance-expat"><Button variant="outline" className="w-full justify-between h-12 rounded-xl bg-white"><span className="flex items-center gap-2"><Plane size={17} className="text-[#8E7FB0]" />Assurance santé expatrié</span><ArrowRight size={16} /></Button></Link>
          </div>
        </Card>
      </section>

      {/* ================= FAQ ================= */}
      <section id="faq" className="bg-slate-50 py-16 md:py-24">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl lg:text-4xl font-bold text-slate-900 text-center">Vos questions, nos réponses</h2>
          <p className="text-slate-500 text-center mt-3">Tout ce qu'il faut savoir avant de vous lancer.</p>
          <Accordion type="single" collapsible className="mt-10">
            {faqs.map((f, i) => (
              <AccordionItem key={i} value={`item-${i}`} className="bg-white rounded-2xl px-5 mb-3 border shadow-sm">
                <AccordionTrigger className="text-left font-semibold text-slate-900 hover:no-underline">{f.q}</AccordionTrigger>
                <AccordionContent className="text-slate-600">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* CTA band */}
      <section className="relative overflow-hidden" style={{ background: "linear-gradient(120deg,#1C1F45,#2E3370)" }}>
        <div className="absolute -right-10 top-0 w-72 h-72 rounded-full bg-indigo-500/25 blur-3xl animate-blob" />
        <div className="relative max-w-5xl mx-auto px-6 py-16 text-center">
          <h2 className="font-display text-3xl lg:text-4xl font-bold text-white">Comparez maintenant, décidez plus tard</h2>
          <p className="text-slate-300 mt-3">C'est gratuit, sans engagement, et ça prend 2 minutes.</p>
          <div className="flex flex-wrap justify-center gap-3 mt-8">
            <Link to="/simulateur/mutuelle-france"><Button className="btn-gradient border-0 rounded-full h-12 px-7 text-base">Comparer les mutuelles santé</Button></Link>
            <Link to="/simulateur/assurance-expat"><Button variant="outline" className="rounded-full h-12 px-7 text-base bg-white/5 text-white border-white/20 hover:bg-white/10 hover:text-white">Comparer les assurances expatrié</Button></Link>
          </div>
        </div>
      </section>
    </PublicLayout>
  );
}
